import { jwtDecode } from 'jwt-decode';
import type { User } from '../types/user';
import { API_URL } from './url';

export enum TokenTypes {
    GOOGLE = 'google',
    LOGIN_LINK = 'login_link',
}

export const getTokens = () => {
    const tokens = localStorage.getItem('auth_tokens');
    return tokens ? JSON.parse(tokens) : {};
};

export const getAccessToken = async () => {
    const tokens = getTokens();
    const token = tokens.access;
    const refreshToken = tokens.refresh;

    if ((!token || isTokenExpired(token)) && refreshToken) {
        await refreshTokens();
        return getTokens().access_token;
    }
    return token;
};

export const setTokens = (tokens: Record<string, string>) => {
    localStorage.setItem('auth_tokens', JSON.stringify(tokens));
};

export const setUser = (user: User) => {
    localStorage.setItem('auth_user', JSON.stringify(user));
};

export const getUser = (): User | null => {
    const user = localStorage.getItem('auth_user');
    return user ? (JSON.parse(user) as User) : null;
};

export const clearUser = () => {
    localStorage.removeItem('auth_user');
};

export const clearTokens = () => {
    localStorage.removeItem('auth_tokens');
    clearUser();
};

export const checkLogin = async () => {
    return !!(await getAccessToken());
};

export const logout = () => {
    clearTokens();
    clearUser();
};

export const refreshTokens = async () => {
    const tokens = getTokens();
    const refreshToken = tokens.refresh;
    fetch(`${API_URL}/auth/refresh_token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: refreshToken }),
    })
        .then((response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error('Failed to refresh tokens');
        })
        .then((data) => {
            setTokens({ ...data, refresh: refreshToken });
        })
        .catch((error) => {
            console.error('Error refreshing tokens:', error);
            logout();
        });
};

export const exchangeToken = async (token: string, type: TokenTypes) => {
    const result = await fetch(`${API_URL}/auth/exchange_token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, token_type: type }),
    });

    if (result.ok) {
        const response = await result.json();
        setTokens(response);
        return;
    }

    throw new Error('Failed to exchange token');
};

export const acceptInvite = async (token: string, firstName: string, lastName: string) => {
    const result = await fetch(`${API_URL}/invitations/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, first_name: firstName, last_name: lastName }),
    });

    if (result.ok) {
        const response = await result.json();
        setTokens(response);
        return;
    }

    throw new Error('Failed to accept invite');
};

function isTokenExpired(token: string) {
    try {
        const { exp } = jwtDecode(token);
        const currentTime = Math.floor(Date.now() / 1000);
        return exp ? exp < currentTime : true;
    } catch (error) {
        console.error('Error decoding token:', error);
        return true;
    }
}
